import { useEffect, useRef, useState } from "react";
import { decodeHtmlEntities } from "constant/htmlEntities";
import { BsClock } from "react-icons/bs";
import { FaPlay } from "react-icons/fa";
import { RiPlayListAddLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
  addPodcastToPodcastPlayer,
  addPodcastToQueue,
} from "redux/AudioPlayerRedux";
import { triggerSuccessNotification } from "utils/notification";
import { formatDurationHrAndMins } from "utils/timeFunctions";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Notification } from "constant/notifications";
import { triggerWarningNotification } from "utils/notification";

function PodcastOneEpisode({ episode }: any) {
  const { currentPodcast } = useSelector((state: any) => state?.audio);
  const dispatch = useDispatch<any>();

  const [isActive, setIsActive] = useState(false);
  const [readMore, setReadMore] = useState<boolean>(false);
  const [slicedDes, setSlicedDes] = useState<string>("");

  const popRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (popRef.current && !popRef.current?.contains(event.target as Node)) {
      setIsActive(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handlePodcast = () => {
    dispatch(addPodcastToPodcastPlayer(episode));
  };

  const handleAddToQueue = () => {
    if (episode) {
      dispatch(addPodcastToQueue(episode));
      triggerSuccessNotification(Notification.PodcastAddedToQueue);
    } else {
      triggerWarningNotification(Notification.NoPodcasts);
    }
  };

  useEffect(() => {
    const description = decodeHtmlEntities(episode?.description);
    if (readMore) {
      setSlicedDes(description);
    } else {
      if (description.length > 180) {
        setSlicedDes(description.slice(0, 180).concat("..."));
      } else {
        setSlicedDes(description);
      }
    }
  }, [episode?.description, readMore]);

  const handleDescription = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    setReadMore((prev) => !prev);
  };

  const description = decodeHtmlEntities(episode?.description);
  const shouldShowReadMore = description.length > 180;
  const isPlayingThis =
    currentPodcast && currentPodcast?.id === episode?.id;

  return (
    <div
      className={`group my-1 w-full cursor-pointer rounded-xl p-4 transition-colors max-sm:p-3 ${
        isPlayingThis ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* left side */}
        <div className="min-w-0 flex-1" onClick={handlePodcast}>
          {/* meta: duration */}
          {episode?.duration ? (
            <div className="mb-1 flex items-center gap-1.5 text-[#7B9DB4]">
              <BsClock className="text-[13px]" />
              <span className="text-[12px] font-[700]">
                {formatDurationHrAndMins(episode?.duration)}
              </span>
            </div>
          ) : null}

          {/* title + playing indicator */}
          <div className="flex items-center gap-3">
            <h3
              className={`min-w-0 text-[20px] font-[700] max-sm:text-[18px] ${
                isPlayingThis ? "text-[var(--red,#F50537)]" : "text-white"
              }`}
            >
              {episode?.title}
            </h3>
            {isPlayingThis && (
              <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-red-600 shadow-lg">
                <FaPlay className="ml-0.5 h-[11px] w-[11px] text-white" />
              </span>
            )}
          </div>

          {/* description */}
          <p className="my-2 max-w-2xl text-[12px] font-[300] leading-relaxed text-white/70 max-sm:w-full">
            {slicedDes}{" "}
            {shouldShowReadMore && (
              <span
                className="cursor-pointer font-medium text-red-500 hover:text-red-400"
                onClick={handleDescription}
              >
                {readMore ? "Read less" : "Read more"}
              </span>
            )}
          </p>
        </div>

        {/* actions */}
        <div className="flex shrink-0 items-start justify-end gap-5 pl-4 text-xl text-[#7B9DB4]">
          <button
            type="button"
            onClick={handleAddToQueue}
            data-tooltip-id="my-tooltip-1"
            aria-label="Add to queue"
            className="transition-colors hover:text-white"
          >
            <RiPlayListAddLine />
          </button>
          <div className="text-sm">
            <ReactTooltip
              id="my-tooltip-1"
              place="bottom"
              content="Add to queue"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PodcastOneEpisode;